/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ScreenType } from 'client-code/vod-browse';

import { ContentType } from '../../../core/enums/ContentType';
import { TransitionScreenType } from '../../../core/enums/TransitionScreenType';
import {
    CollectionScreenError,
    ContentScreenDetailsError,
    OfferContentScreenDetailsError,
    RecordingContentScreenDetailsError,
    VodOfferContentScreenDetailsError,
} from '../../../core/error';
import { NdvrInteractor, VodInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import {
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
    ScreenTaskControllerTypes,
} from '../../../core/navigation';
import { Screen } from '../../../core/navigation/Screen';
import { ScreenController } from '../../../core/navigation/ScreenController';
import { TScreen } from '../../../core/navigation/types';
import { TGalleryScreenState } from '../../../core/types';
import { TProgramScreenState } from '../../../core/types/TProgramScreenState';
import { GalleryScreenComponent } from '../../../core/usecases';
import { CcbSwitch } from '../../../core/utils/ccb';
import { CcbSwitchName } from '../../../core/utils/ccb/enums';
import { GridTypes } from '../../../core/utils/GridTypes';
import { BiaxialActionWidgetHelper } from '../../../views/ProgramScreen/BiaxialActionWidget/BiaxialActionWidgetHelper';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { resetMyShows } from '../../myshows/myshows.actions';
import { MyShowsListGenerator } from '../../myshows/MyShowsListGenerator';
import {
    setCurrentVerticalHighlightIndexProgramScreenAction,
    setHorizontalOptionsProgramScreenAction,
} from '../../programscreen/programscreen.actions';
import { showCollectionScreen, showGalleryScreen, showProgramScreen } from '../screens.reducers';

const TAG: string = 'PreCheckForScreenTransition';

const preCheckForProgramScreenTransition = async (
    id: string,
    contentType: ContentType,
    partnerId?: string,
    collapsedVodItemId?: string,
    contentSupplierPartnerId?: string,
) => {
    const ndvrServices = new NdvrInteractor();
    const vodServices = new VodInteractor();

    const actionErrorMap = {
        [ContentType.RECORDING_ID]: {
            action: () => ndvrServices.getContentScreenDetailsRecordingActions({ recordingId: id }),
            errorClass: RecordingContentScreenDetailsError,
        },
        [ContentType.CONTENT_ID]: {
            action: () =>
                vodServices.getContentScreenDetailsContentActionsV2({
                    contentId: id,
                    screenType: 'ProgramScreen',
                    partnerId,
                    collapsedVodItemId,
                    contentSupplierPartnerId,
                }),
            errorClass: ContentScreenDetailsError,
        },
        [ContentType.OFFER_ID]: {
            action: () => ndvrServices.getContentScreenDetailsOfferActions({ offerId: id }),
            errorClass: OfferContentScreenDetailsError,
        },
        [ContentType.VOD_OFFER_ID]: {
            action: () =>
                ndvrServices.getContentScreenDetailsVodOfferActionsV2({
                    vodOfferId: id,
                    collapsedVodItemId,
                    screenType: 'ProgramScreen',
                }),
            errorClass: VodOfferContentScreenDetailsError,
        },
    };

    try {
        await actionErrorMap[contentType]?.action();
    } catch (error: any) {
        Log.e(TAG, error.message);
        Diagnostics.error(TAG, error.message);

        const ErrorClass = actionErrorMap[contentType]?.errorClass;
        const errorCode = new ErrorClass(
            error?.response?.data?.code,
            error?.response?.data?.message,
        ).getCode();

        const errorMessageToShow = Locale.getString(
            LocaleKeys.WHISPER_SCREEN_DETAILS_UNAVAILABLE,
        ).replace('ERROR_CODE', errorCode);

        throw new Error(errorMessageToShow);
    }
};

const preCheckForCollectionScreenTransition = async (
    id: string,
    partnerId?: string,
    collapsedVodItemId?: string,
    contentSupplierPartnerId?: string,
) => {
    try {
        const ndvrServices = new NdvrInteractor();
        await ndvrServices.getUCScreenDetailsActionsV2({
            unifiedCollectionId: id,
            partnerId,
            collapsedVodItemId,
            contentSupplierPartnerId,
        });
    } catch (error: any) {
        const errorMessage = error.message;
        const ucsadError = new CollectionScreenError(
            error?.response?.data?.code,
            error?.response?.data?.message,
        );
        Log.e(TAG, errorMessage);
        Diagnostics.error(TAG, errorMessage);
        const errorMessageToShow = Locale.getString(
            LocaleKeys.WHISPER_SCREEN_DETAILS_UNAVAILABLE,
        ).replace('ERROR_CODE', ucsadError.getCode());
        throw new Error(errorMessageToShow);
    }
};

export const collectionScreenTransition =
    (
        collectionScreenInfo: {
            unifiedCollectionId: string;
            transitionScreen: TransitionScreenType;
            partnerId?: string;
            collapsedVodItemId?: string;
            contentSupplierPartnerId?: string;
        },
        callback?: Function,
    ) =>
    async (dispatch: Function) => {
        try {
            const {
                unifiedCollectionId,
                transitionScreen,
                partnerId,
                collapsedVodItemId,
                contentSupplierPartnerId,
            } = collectionScreenInfo;

            let partnerIdParam = partnerId;
            let collapsedVodItemIdParam = collapsedVodItemId;
            let contentSupplierPartnerIdParam = contentSupplierPartnerId;

            if (
                !CcbSwitch.getInstance().isEnabledAndOverriden(
                    CcbSwitchName.ENABLE_VOD_SLT_REMOVE_IN_KST_12207,
                )
            ) {
                partnerIdParam = undefined;
                collapsedVodItemIdParam = undefined;
                contentSupplierPartnerIdParam = undefined;
            }

            if (!KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().startKeyIgnoring();

            await preCheckForCollectionScreenTransition(
                unifiedCollectionId,
                partnerIdParam,
                collapsedVodItemIdParam,
                contentSupplierPartnerIdParam,
            );
            dispatch(
                showCollectionScreen({
                    unifiedCollectionId,
                    transitionScreen,
                    partnerId: partnerIdParam,
                    collapsedVodItemId: collapsedVodItemIdParam,
                    contentSupplierPartnerId: contentSupplierPartnerIdParam,
                }),
            );
            MyShowsListGenerator.reset();
            dispatch(resetMyShows());

            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
        } catch (error: any) {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();

            if (callback)
                dispatch(callback({ message: error.message, iconType: WhisperTypes.NO_ICON }));
        }
    };

export const galleryScreenTransition =
    (
        isAdult: boolean,
        parentVodItemId: string,
        partnerId: string,
        screenType: ScreenType,
        title: string,
    ) =>
    async (dispatch: Function, getState: Function) => {
        if (!KeyManager.getInstance().isKeyIgnoring()) KeyManager.getInstance().startKeyIgnoring();
        const currScreen = ScreenController.getInstance().getScreen<TScreen>();
        const currScreenProperties = currScreen.getProperties();
        // updating indexes in previous screen before pushing new screen
        if (currScreen.getIdentifier().includes('GalleryScreen')) {
            const focusedItem = getState().grid[GridTypes.GALLERY]?.focusedItem || {
                row: 0,
                column: 0,
            };
            const { row, column } = focusedItem;
            const currentPage = GalleryScreenComponent.getCurrentPage();
            currScreen.setProperties({
                ...currScreenProperties,
                row,
                column,
                page: currentPage,
            } as TGalleryScreenState & TScreen);
        }
        // pushing new screen
        const newScreen = new Screen<TGalleryScreenState & TScreen>(
            `GalleryScreen-${parentVodItemId}}`,
        );
        const screenProperties: TGalleryScreenState & TScreen = {
            isAdult,
            parentVodItemId,
            partnerId,
            screenType,
            title,
            url: `${LocationUrls.GALLERY_SCREEN}/${parentVodItemId}`,
            screenController: ScreenControllerTypes.GALLERY_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS,
            row: 0,
            column: 0,
            offset: 0,
            page: 0,
        };
        newScreen.setProperties(screenProperties);
        ScreenController.getInstance().push(newScreen);
        dispatch(showGalleryScreen(parentVodItemId));
    };

export const programScreenTransition =
    (
        programScreenInfo: {
            id: string;
            contentType: ContentType;
            partnerId?: string;
            collapsedVodItemId?: string;
            contentSupplierPartnerId?: string;
        },
        callback?: Function,
        screenPropertiesOverride?: Partial<TProgramScreenState & TScreen>,
    ) =>
    async (dispatch: Function, getState: Function) => {
        if (!KeyManager.getInstance().isKeyIgnoring()) KeyManager.getInstance().startKeyIgnoring();

        const { id, contentType, partnerId, collapsedVodItemId, contentSupplierPartnerId } =
            programScreenInfo;

        let partnerIdParam = partnerId;
        let collapsedVodItemIdParam = collapsedVodItemId;
        let contentSupplierPartnerIdParam = contentSupplierPartnerId;

        if (
            !CcbSwitch.getInstance().isEnabledAndOverriden(
                CcbSwitchName.ENABLE_VOD_BROWSE_PROGRAM_SCREEN_REMOVE_IN_KST_12208,
            )
        ) {
            partnerIdParam = undefined;
            collapsedVodItemIdParam = undefined;
            contentSupplierPartnerIdParam = undefined;
        }

        try {
            await preCheckForProgramScreenTransition(
                id,
                contentType,
                partnerIdParam,
                collapsedVodItemIdParam,
                contentSupplierPartnerIdParam,
            );
            const newScreen = new Screen<TProgramScreenState & TScreen>(
                `ProgramScreen-${id}-${Date.now()}`,
            );
            const currScreen = ScreenController.getInstance().getScreen();
            const currScreenProperties = ScreenController.getInstance()
                .getScreen()
                .getProperties()!;
            currScreen.setProperties({
                ...currScreenProperties,
                horizontalIndex: getState().programScreen.currentHighlightIndex,
                verticalIndex: getState().programScreen.currentVerticalHighlightIndex,
                currentImageIndex: BiaxialActionWidgetHelper.getCurrentImageIndex(),
            });
            const screenProperties: TProgramScreenState & TScreen = {
                url: `${LocationUrls.PROGRAM_SCREEN}/${Date.now()}`,
                screenController: ScreenControllerTypes.PROGRAM_SCREEN,
                taskController: ScreenTaskControllerTypes.DEFAULT_PROGRAM_SCREEN_TASKS,
                contentType,
                horizontalIndex: 0,
                id,
                verticalIndex: 0,
                currentImageIndex: 0,
                partnerId: partnerIdParam,
                collapsedVodItemId: collapsedVodItemIdParam,
                contentSupplierPartnerId: contentSupplierPartnerIdParam,
                ...screenPropertiesOverride,
            };
            dispatch(setCurrentVerticalHighlightIndexProgramScreenAction(0));
            newScreen.setProperties(screenProperties);
            ScreenController.getInstance().push(newScreen);
            dispatch(setHorizontalOptionsProgramScreenAction([]));
            dispatch(showProgramScreen());
            MyShowsListGenerator.reset();
            dispatch(resetMyShows());
        } catch (error: any) {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
            if (callback)
                dispatch(callback({ message: error.message, iconType: WhisperTypes.NO_ICON }));
        }
    };
